import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';

import MigrationSection, {
	type DryRunRecord,
	type MigrationRecord,
	useMigrationMatching,
} from './MigrationSection';

export default function DepartmentsSection() {
	const { t } = useTranslation();
	const departmentsQuery = graphql.useMigrationDepartmentsQuery({
		variables: {
			filter: { types: [graphql.GroupType.Department], isArchived: false },
			first: 10000,
		},
	});
	const dryRunQuery = graphql.useConnectorDryRunGroupsQuery({
		variables: { first: 10000 },
	});
	const [updateGroup] = graphql.useUpdateGroupMutation();

	const existing = useMemo(
		(): MigrationRecord[] =>
			[...(departmentsQuery.data?.groups.edges ?? [])]
				.sort((a, b) => a.node.name.localeCompare(b.node.name))
				.map(({ node }) => ({
					id: node.id,
					externalReferenceID: node.externalReferenceID,
					matchKey: node.name,
					name: node.name,
				})),
		[departmentsQuery.data],
	);

	const dryRun = useMemo(
		(): DryRunRecord[] =>
			[...(dryRunQuery.data?.viewer.workspace.connector?.dryRun.groups.edges ?? [])]
				.sort((a, b) => a.node.name.localeCompare(b.node.name))
				.map(({ node, cursor }) => ({
					key: cursor,
					externalReferenceID: node.externalReferenceID,
					matchKey: node.name,
					name: node.name,
				})),
		[dryRunQuery.data],
	);

	const { matched, unmatched, newItems } = useMigrationMatching(existing, dryRun);

	const departmentMap = useMemo(
		() => new Map((departmentsQuery.data?.groups.edges ?? []).map(({ node }) => [node.id, node])),
		[departmentsQuery.data],
	);

	const handleSave = async (id: string, externalReferenceID: string) => {
		const department = departmentMap.get(id);
		if (!department) throw new Error('Department not found');
		await updateGroup({
			variables: {
				input: {
					id: department.id,
					name: department.name,
					externalReferenceID,
				},
			},
		});
	};

	return (
		<MigrationSection
			title={t('connectors.settings.migration.departments.title')}
			loading={departmentsQuery.loading || dryRunQuery.loading}
			matched={matched}
			newItems={newItems}
			unmatched={unmatched}
			onSave={handleSave}
			onRefetch={() => departmentsQuery.refetch()}
			emptyMessage={t('connectors.settings.migration.departments.empty')}
		/>
	);
}
